/* eslint-disable */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, RefreshCw, Download, Upload, Loader2, Plus, Edit3, History, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi, getAdminToken } from "@/lib/adminApi";
import { BACKEND_URL } from "@/lib/api";
import BulkUploadModal from "@/components/admin/BulkUploadModal";
import BulkUploadHistoryModal from "@/components/admin/BulkUploadHistoryModal";
import SotCleanupModal from "@/components/admin/SotCleanupModal";

// The 50 U.S. jurisdictions Regulens currently covers. Source of truth lives
// in ManageJurisdictionsModal but we duplicate here to avoid a dashboard-only
// import in the admin component.
const COVERED_STATES = [
  ["AK", "Alaska"], ["AL", "Alabama"], ["AR", "Arkansas"], ["AZ", "Arizona"],
  ["CA", "California"], ["CO", "Colorado"], ["DC", "Washington D.C."],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["IA", "Iowa"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MI", "Michigan"],
  ["MN", "Minnesota"], ["MO", "Missouri"], ["MS", "Mississippi"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["NE", "Nebraska"],
  ["NM", "New Mexico"], ["NV", "Nevada"], ["NY", "New York"], ["OK", "Oklahoma"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"],
  ["TX", "Texas"], ["VA", "Virginia"], ["VT", "Vermont"], ["WA", "Washington"],
  ["WI", "Wisconsin"], ["WV", "West Virginia"], ["WY", "Wyoming"],
];

const emptyTaxRule = () => ({
  state_name: "",
  abbreviation: "",
  authority_name: "",
  official_url: "",
  statute_citation: "",
  last_verified_date: new Date().toISOString().slice(0, 10),
  special_notes: "",
  revenue_threshold: 0,
  transaction_threshold: 0,
  has_sales_tax: true,
  is_disabled: false,
  disabled_reason: "",
});

const emptyPrivacyRule = () => ({
  state_name: "",
  abbreviation: "",
  enforcement_authority: "",
  official_url: "",
  statute_citation: "",
  last_verified_date: new Date().toISOString().slice(0, 10),
  special_notes: "",
  law_name: "",
  is_disabled: false,
  disabled_reason: "",
});

export default function AdminDatabaseTab() {
  const [data, setData] = useState({ tax: [], privacy: [] });
  const [activeColl, setActiveColl] = useState("tax");
  const [buffer, setBuffer] = useState({}); // { "tax::CA": { field: newVal, ... } }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ruleModal, setRuleModal] = useState(null); // { mode: "new" | "edit", collection, row }
  const [ruleSaving, setRuleSaving] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkHistoryOpen, setBulkHistoryOpen] = useState(false);
  const [sotCleanupOpen, setSotCleanupOpen] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/admin/jurisdictions");
    setData(data);
  };

  useEffect(() => { load().catch(() => toast.error("Could not load jurisdictions")); }, []);

  // Ctrl/Cmd+S → open deploy dialog
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (Object.keys(buffer).length > 0) setConfirmOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [buffer]);

  const editCell = (collection, abbr, field, value) => {
    const key = `${collection}::${abbr}`;
    setBuffer((b) => ({ ...b, [key]: { ...(b[key] || {}), [field]: value } }));
  };

  const isEdited = (collection, abbr, field) => {
    const key = `${collection}::${abbr}`;
    return buffer[key]?.[field] !== undefined;
  };

  const discardAll = () => {
    if (!confirm("Discard all unsaved changes?")) return;
    setBuffer({});
  };

  const deploy = async () => {
    setBusy(true);
    const changes = Object.entries(buffer).map(([key, updates]) => {
      const [collection, abbreviation] = key.split("::");
      return { collection, abbreviation, updates };
    });
    try {
      const { data } = await adminApi.post("/admin/jurisdictions/deploy", changes);
      toast.success(`Deployed ${data.applied} change(s)`);
      setBuffer({});
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deploy failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = () => {
    const t = getAdminToken();
    const url = `${BACKEND_URL}/api/admin/jurisdictions/export-csv?collection=${activeColl}`;
    // Use fetch to attach the admin header, then download
    fetch(url, { headers: { "X-Admin-Token": t } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `regulens_${activeColl}.csv`;
        a.click();
      });
  };

  const openNewRule = () => {
    const blank = activeColl === "tax" ? emptyTaxRule() : emptyPrivacyRule();
    setRuleModal({ mode: "new", collection: activeColl, row: blank });
  };

  const openEditRule = (row) => {
    setRuleModal({
      mode: "edit",
      collection: activeColl,
      row: {
        ...row,
        // ensure numeric fields are numbers for the inputs
        revenue_threshold: row.revenue_threshold ?? 0,
        transaction_threshold: row.transaction_threshold ?? 0,
      },
    });
  };

  // Item 3 — Bulk "Mark All Verified Today" for the active sub-tab only.
  const [bulkVerifyOpen, setBulkVerifyOpen] = useState(false);
  const [bulkVerifyBusy, setBulkVerifyBusy] = useState(false);
  const bulkMarkVerified = async () => {
    setBulkVerifyBusy(true);
    try {
      const { data } = await adminApi.post("/admin/jurisdictions/mark-collection-verified", {
        collection: activeColl,
      });
      toast.success(
        `All ${data.matched_count} ${activeColl === "tax" ? "Sales Tax" : "Privacy Law"} states marked as verified today.`,
      );
      setBulkVerifyOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk verify failed");
    } finally {
      setBulkVerifyBusy(false);
    }
  };

  const setRuleField = (field, value) => {
    setRuleModal((m) => (m ? { ...m, row: { ...m.row, [field]: value } } : m));
  };

  const setRuleJurisdiction = (abbr) => {
    const entry = COVERED_STATES.find(([a]) => a === abbr);
    if (!entry) return;
    setRuleModal((m) => (m ? { ...m, row: { ...m.row, abbreviation: abbr, state_name: entry[1] } } : m));
  };

  const saveRule = async () => {
    if (!ruleModal) return;
    const { mode, collection, row } = ruleModal;
    if (!row.abbreviation) {
      toast.error("Jurisdiction is required");
      return;
    }
    setRuleSaving(true);
    try {
      // Coerce thresholds to integers for tax rules.
      const sanitized = { ...row };
      if (collection === "tax") {
        sanitized.revenue_threshold = parseInt(sanitized.revenue_threshold) || 0;
        sanitized.transaction_threshold = parseInt(sanitized.transaction_threshold) || 0;
        sanitized.transaction_threshold_active = sanitized.transaction_threshold > 0;
      }
      if (mode === "new") {
        await adminApi.post("/admin/jurisdictions/add", { collection, row: sanitized });
        toast.success("Rule added (PENDING — invisible to users until verified).");
      } else {
        // Edit mode — push through /update endpoint
        // Strip identity fields from updates payload (not changed)
        const { abbreviation, ...updates } = sanitized;
        await adminApi.post("/admin/jurisdictions/update", { collection, abbreviation, updates });
        toast.success("Rule updated.");
      }
      setRuleModal(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setRuleSaving(false);
    }
  };

  const usedAbbrs = (activeColl === "tax" ? data.tax : data.privacy).map((r) => r.abbreviation);

  const rows = activeColl === "tax" ? data.tax : data.privacy;
  const totalEdits = Object.values(buffer).reduce((n, m) => n + Object.keys(m).length, 0);

  return (
    <div className="space-y-4" data-testid="admin-db-tab">
      {totalEdits > 0 && (
        <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] px-4 py-3 flex items-center justify-between sticky top-2 z-10" data-testid="admin-unsaved-banner">
          <div className="text-sm text-[var(--rg-text-strong)]">
            <span className="font-bold text-[var(--rg-amber)]">{totalEdits} unsaved change{totalEdits !== 1 ? "s" : ""}</span>
            <span className="text-[var(--rg-text)] ml-2">Press Ctrl/Cmd+S to deploy.</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={discardAll} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="admin-discard-all">
              <Trash2 className="w-4 h-4 mr-1.5" /> Discard
            </Button>
            <Button size="sm" onClick={() => setConfirmOpen(true)} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" title="Ctrl+S" data-testid="admin-deploy-btn">
              <Save className="w-4 h-4 mr-1.5" /> Deploy {totalEdits}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <Button size="sm" onClick={() => setActiveColl("tax")} className={activeColl === "tax" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-subtab-tax">Sales Tax</Button>
        <Button size="sm" onClick={() => setActiveColl("privacy")} className={activeColl === "privacy" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)]" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)]"} data-testid="admin-subtab-privacy">Privacy</Button>
        <Button
          size="sm"
          onClick={() => setBulkVerifyOpen(true)}
          className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-semibold"
          data-testid={`admin-mark-all-verified-${activeColl}`}
          title={`Update last_verified date to today for all states in the ${activeColl === "tax" ? "Sales Tax" : "Privacy Law"} tab.`}
        >
          Mark All Verified Today
        </Button>
        <Button
          size="sm"
          onClick={() => setSotCleanupOpen(true)}
          variant="outline"
          className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage-soft)] font-semibold"
          data-testid="admin-sot-cleanup-btn"
          title="Reset source_of_truth back to CSV for rows that were incorrectly flagged admin_edited by routine actions. Preserves genuine hand-edits."
        >
          <ShieldCheck className="w-4 h-4 mr-1.5" /> Reset SoT Flags
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={openNewRule} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-new-rule-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Rule
          </Button>
          <Button size="sm" variant="outline" onClick={downloadCsv} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="admin-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Download CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkUploadOpen(true)} className="border-[var(--rg-sage)] text-[var(--rg-sage)] hover:bg-[var(--rg-sage)]/10" data-testid="admin-bulk-upload-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => setBulkHistoryOpen(true)} className="border-[var(--rg-amber)] text-[var(--rg-amber)] hover:bg-[var(--rg-amber-soft)]" data-testid="admin-bulk-history-btn">
            <History className="w-4 h-4 mr-1.5" /> Upload History
          </Button>
        </div>
      </div>

      <BulkUploadModal
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        collection={activeColl}
        onApplied={load}
      />

      <BulkUploadHistoryModal
        open={bulkHistoryOpen}
        onClose={() => setBulkHistoryOpen(false)}
        collection={activeColl}
        onReverted={load}
      />

      <SotCleanupModal
        open={sotCleanupOpen}
        onClose={() => setSotCleanupOpen(false)}
        onApplied={load}
      />

      <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[var(--rg-muted)] border-b border-[var(--rg-border)]">
              <th className="py-2.5 px-3">State</th>
              <th className="py-2.5 px-3">Authority</th>
              <th className="py-2.5 px-3">Source URL</th>
              <th className="py-2.5 px-3">Last Verified</th>
              <th className="py-2.5 px-3">Special Notes</th>
              <th className="py-2.5 px-3">Disabled</th>
              {activeColl === "privacy" && <th className="py-2.5 px-3">Scoring Policy</th>}
              <th className="py-2.5 px-3 w-16">Edit</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${activeColl}::${r.abbreviation}`;
              const merged = { ...r, ...(buffer[key] || {}) };
              return (
                <tr key={r.abbreviation} className="border-b border-[var(--rg-border)]/60">
                  <td className="py-2 px-3 text-[var(--rg-text-strong)] whitespace-nowrap">
                    {merged.state_name}
                    <span className="text-[var(--rg-muted)] font-mono text-xs ml-1">({merged.abbreviation})</span>
                    {activeColl === "tax" && <ThresholdOperatorBadge rule={merged} />}
                    {merged.source_of_truth === "admin_edited" && (
                      <span
                        className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider text-[var(--rg-sage)] border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)]"
                        title="This row is protected — seed_jurisdictions() will skip it on the next restart. Click 'Reset to CSV' below the table to unflag."
                        data-testid={`admin-sot-badge-${r.abbreviation}`}
                      >
                        Admin-Edited
                      </span>
                    )}
                    {buffer[key] && Object.keys(buffer[key]).length > 0 && (
                      <span
                        className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider text-[var(--rg-amber)] border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)]"
                        data-testid={`admin-staged-chip-${r.abbreviation}`}
                      >
                        Staged
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      value={merged.authority_name || merged.enforcement_authority || ""}
                      onChange={(e) => editCell(activeColl, r.abbreviation, activeColl === "tax" ? "authority_name" : "enforcement_authority", e.target.value)}
                      className={`h-8 text-xs ${isEdited(activeColl, r.abbreviation, activeColl === "tax" ? "authority_name" : "enforcement_authority") ? "bg-[var(--rg-amber-soft)] border-[var(--rg-amber)]" : "bg-[var(--rg-surface)] border-[var(--rg-border)]"} text-[var(--rg-text-strong)]`}
                      data-testid={`admin-cell-${r.abbreviation}-authority`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      value={merged.official_url || ""}
                      onChange={(e) => editCell(activeColl, r.abbreviation, "official_url", e.target.value)}
                      className={`h-8 text-xs font-mono ${isEdited(activeColl, r.abbreviation, "official_url") ? "bg-[var(--rg-amber-soft)] border-[var(--rg-amber)]" : "bg-[var(--rg-surface)] border-[var(--rg-border)]"} text-[var(--rg-text-strong)]`}
                      data-testid={`admin-cell-${r.abbreviation}-url`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Input
                      value={merged.last_verified_date || ""}
                      onChange={(e) => editCell(activeColl, r.abbreviation, "last_verified_date", e.target.value)}
                      className={`h-8 text-xs font-mono w-32 ${isEdited(activeColl, r.abbreviation, "last_verified_date") ? "bg-[var(--rg-amber-soft)] border-[var(--rg-amber)]" : "bg-[var(--rg-surface)] border-[var(--rg-border)]"} text-[var(--rg-text-strong)]`}
                      data-testid={`admin-cell-${r.abbreviation}-date`}
                    />
                  </td>
                  <td className="py-2 px-3 min-w-[260px]">
                    <Textarea
                      value={merged.special_notes || ""}
                      onChange={(e) => editCell(activeColl, r.abbreviation, "special_notes", e.target.value)}
                      rows={2}
                      placeholder="e.g., NY dual-threshold, FL FDBR <$1B exemption…"
                      className={`text-xs ${isEdited(activeColl, r.abbreviation, "special_notes") ? "bg-[var(--rg-amber-soft)] border-[var(--rg-amber)]" : "bg-[var(--rg-surface)] border-[var(--rg-border)]"} text-[var(--rg-text-strong)] min-h-[44px]`}
                      data-testid={`admin-cell-${r.abbreviation}-notes`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    <Switch checked={!!merged.is_disabled} onCheckedChange={(v) => editCell(activeColl, r.abbreviation, "is_disabled", v)} />
                  </td>
                  {activeColl === "privacy" && (
                    <td className="py-2 px-3">
                      <ScoringPolicySelect
                        abbr={r.abbreviation}
                        current={merged.scoring_policy || "PENDING_REVIEW"}
                        onChanged={load}
                      />
                    </td>
                  )}
                  <td className="py-2 px-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditRule(r)}
                      className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)] h-7 px-2"
                      data-testid={`admin-edit-rule-${r.abbreviation}`}
                      title="Open full edit form"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Add/Edit Compliance Rule modal ---- */}
      <Dialog open={!!ruleModal} onOpenChange={(o) => !o && !ruleSaving && setRuleModal(null)}>
        <DialogContent
          className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="admin-rule-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">
              {ruleModal?.mode === "new" ? "Add Compliance Rule" : `Edit Rule — ${ruleModal?.row?.state_name || ruleModal?.row?.abbreviation || ""}`}
              <span className="ml-2 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--rg-muted)]">
                ({ruleModal?.collection === "tax" ? "Sales Tax Nexus" : "Privacy Law"})
              </span>
            </DialogTitle>
          </DialogHeader>

          {ruleModal && (
            <div className="space-y-3">
              {/* Jurisdiction dropdown — locked on edit */}
              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Jurisdiction</Label>
                <Select
                  value={ruleModal.row.abbreviation || ""}
                  onValueChange={setRuleJurisdiction}
                  disabled={ruleModal.mode === "edit"}
                >
                  <SelectTrigger
                    className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 disabled:opacity-70"
                    data-testid="rule-jurisdiction-select"
                  >
                    <SelectValue placeholder="Select a U.S. jurisdiction" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-h-72">
                    {COVERED_STATES.map(([abbr, name]) => {
                      const taken = ruleModal.mode === "new" && usedAbbrs.includes(abbr);
                      return (
                        <SelectItem
                          key={abbr}
                          value={abbr}
                          disabled={taken}
                          data-testid={`rule-juris-opt-${abbr}`}
                        >
                          {name} ({abbr}) {taken && <span className="text-[var(--rg-muted)] text-[10px] ml-1">— already exists</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {ruleModal.mode === "edit" && (
                  <p className="text-[10px] text-[var(--rg-muted)] mt-1 font-mono">
                    Jurisdiction is locked for existing rules. Delete & re-add to change state.
                  </p>
                )}
              </div>

              {/* Authority */}
              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">
                  {ruleModal.collection === "tax" ? "Tax authority name" : "Enforcement authority"}
                </Label>
                <Input
                  value={
                    ruleModal.collection === "tax"
                      ? (ruleModal.row.authority_name || "")
                      : (ruleModal.row.enforcement_authority || "")
                  }
                  onChange={(e) => setRuleField(
                    ruleModal.collection === "tax" ? "authority_name" : "enforcement_authority",
                    e.target.value,
                  )}
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
                  data-testid="rule-authority"
                />
              </div>

              {/* Privacy-only law name */}
              {ruleModal.collection === "privacy" && (
                <div>
                  <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Law name</Label>
                  <Input
                    value={ruleModal.row.law_name || ""}
                    onChange={(e) => setRuleField("law_name", e.target.value)}
                    placeholder="e.g., CCPA / CPRA"
                    className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
                    data-testid="rule-law-name"
                  />
                </div>
              )}

              {/* Tax-only — split Revenue + Transaction thresholds */}
              {ruleModal.collection === "tax" && (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="rule-thresholds-row">
                  <div>
                    <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Revenue Threshold ($)</Label>
                    <Input
                      type="number"
                      min={0}
                      step={1000}
                      value={ruleModal.row.revenue_threshold ?? 0}
                      onChange={(e) => setRuleField("revenue_threshold", e.target.value)}
                      placeholder="100000"
                      className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                      data-testid="rule-revenue-threshold"
                    />
                  </div>
                  <div>
                    <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Transaction Threshold (count)</Label>
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      value={ruleModal.row.transaction_threshold ?? 0}
                      onChange={(e) => setRuleField("transaction_threshold", e.target.value)}
                      placeholder="200"
                      className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                      data-testid="rule-transaction-threshold"
                    />
                    <p className="text-[10px] text-[var(--rg-muted)] mt-1 font-mono">
                      0 = no transaction-count threshold (revenue-only state)
                    </p>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Statute citation</Label>
                <Input
                  value={ruleModal.row.statute_citation || ""}
                  onChange={(e) => setRuleField("statute_citation", e.target.value)}
                  placeholder="e.g., Cal. Rev. & Tax. Code § 6203"
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                  data-testid="rule-citation"
                />
              </div>

              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Official source URL</Label>
                <Input
                  value={ruleModal.row.official_url || ""}
                  onChange={(e) => setRuleField("official_url", e.target.value)}
                  placeholder="https://..."
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono"
                  data-testid="rule-url"
                />
              </div>

              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Last verified date</Label>
                <Input
                  type="date"
                  value={ruleModal.row.last_verified_date || ""}
                  onChange={(e) => setRuleField("last_verified_date", e.target.value)}
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1 font-mono w-44"
                  data-testid="rule-last-verified"
                />
              </div>

              <div>
                <Label className="text-[var(--rg-text)] text-xs uppercase tracking-[0.18em]">Special notes</Label>
                <Textarea
                  value={ruleModal.row.special_notes || ""}
                  onChange={(e) => setRuleField("special_notes", e.target.value)}
                  rows={3}
                  placeholder="e.g., Tier-3. Revenue-only. Standard enforcement."
                  className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] mt-1"
                  data-testid="rule-special-notes"
                />
              </div>

              {ruleModal.mode === "new" && (
                <p className="text-[10px] text-[var(--rg-amber)] font-mono">
                  New rules are created with status <strong>PENDING</strong> — invisible to users until verified.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRuleModal(null)}
              disabled={ruleSaving}
              className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
              data-testid="rule-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={saveRule}
              disabled={ruleSaving || !ruleModal?.row?.abbreviation}
              className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold disabled:opacity-40"
              data-testid="rule-save"
            >
              {ruleSaving ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving…</>
              ) : ruleModal?.mode === "new" ? (
                "Create Rule"
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl" data-testid="admin-deploy-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Deploy {totalEdits} change{totalEdits !== 1 ? "s" : ""} to live database?</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto border border-[var(--rg-border)] rounded-md bg-[var(--rg-surface)] p-3 space-y-1 text-xs font-mono">
            {Object.entries(buffer).flatMap(([key, fields]) =>
              Object.entries(fields).map(([f, v]) => (
                <div key={key + "::" + f} className="text-[var(--rg-text)]">
                  <span className="text-[var(--rg-sage)]">{key}</span> · {f} → <span className="text-[var(--rg-text-strong)]">{String(v)}</span>
                </div>
              )),
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]">Cancel</Button>
            <Button onClick={deploy} disabled={busy} className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="admin-deploy-confirm">
              {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Deploying…</> : "Confirm and Deploy"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item 3 — Bulk "Mark All Verified Today" confirmation */}
      <Dialog open={bulkVerifyOpen} onOpenChange={(o) => !o && !bulkVerifyBusy && setBulkVerifyOpen(false)}>
        <DialogContent className="bg-[var(--rg-surface)] border-[var(--rg-amber)] text-[var(--rg-text-strong)] max-w-md" data-testid="admin-bulk-verify-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]">Mark all verified today?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[var(--rg-text)] space-y-2">
            <p>
              Update <span className="font-bold text-[var(--rg-text-strong)]">last_verified</span> date to today for all 50 states in the{" "}
              <span className="font-bold text-[var(--rg-text-strong)]">
                {activeColl === "tax" ? "Sales Tax" : "Privacy Law"}
              </span>{" "}
              tab?
            </p>
            <p className="text-xs text-[var(--rg-muted)]">
              This writes to the database immediately in a single bulk operation.
            </p>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setBulkVerifyOpen(false)}
              className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]"
              data-testid="admin-bulk-verify-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={bulkMarkVerified}
              disabled={bulkVerifyBusy}
              className="bg-[var(--rg-amber)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-amber)] font-bold"
              data-testid="admin-bulk-verify-confirm"
            >
              {bulkVerifyBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Updating…</> : "Mark All Today"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/**
 * ThresholdOperatorBadge — tiny visual affordance next to each tax jurisdiction
 * showing whether nexus evaluates by revenue only, an OR dual-test, or an AND
 * dual-test. Also flags a safety-valve override when active.
 */
function ThresholdOperatorBadge({ rule }) {
  const override = !!rule?.operator_override_revenue_only;
  const op = override ? "REVENUE_ONLY" : (rule?.threshold_operator || "REVENUE_ONLY");
  const styles = {
    REVENUE_ONLY: { color: "var(--rg-muted)", border: "var(--rg-muted)", bg: "var(--rg-surface-2)", label: "Revenue" },
    OR:           { color: "var(--rg-sage)", border: "var(--rg-sage)", bg: "var(--rg-sage-soft)", label: "OR" },
    AND:          { color: "var(--rg-amber)", border: "var(--rg-amber)", bg: "var(--rg-amber-soft)", label: "AND" },
  };
  const s = styles[op] || styles.REVENUE_ONLY;
  const rev = rule?.revenue_threshold || 0;
  const txn = rule?.transaction_threshold || 0;
  const title = op === "AND"
    ? `Both must be met: revenue ≥ $${rev.toLocaleString()} AND ≥ ${txn} transactions`
    : op === "OR"
      ? `Either triggers: revenue ≥ $${rev.toLocaleString()} OR ≥ ${txn} transactions`
      : `Revenue-only: nexus triggers when revenue ≥ $${rev.toLocaleString()}`;
  return (
    <>
      <span
        className="ml-2 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider"
        style={{ color: s.color, border: `1px solid ${s.border}`, background: s.bg }}
        title={title}
        data-testid={`admin-op-badge-${rule?.abbreviation || ""}`}
      >
        {s.label}
      </span>
      {override && (
        <span
          className="ml-1 inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider"
          style={{ color: "var(--rg-red)", border: "1px solid var(--rg-red)", background: "var(--rg-red-soft)" }}
          title="Statute operator overridden to REVENUE_ONLY via operator_override_revenue_only. See special_notes."
          data-testid={`admin-op-badge-override-${rule?.abbreviation || ""}`}
        >
          Override
        </span>
      )}
    </>
  );
}


/**
 * ScoringPolicySelect — Stage 3 (Prompt B) per-state gating toggle.
 * PENDING_REVIEW = default; state falls into "not_yet_scored" (or Provisional
 *   if merchant profile is missing new signals).
 * ACTIVE = state participates in Pillar 2 scoring (Stage 4+).
 * DISABLED = admin-suspended; same behaviour as PENDING_REVIEW but
 *   signals intent (e.g., after a bad-data rollback).
 * Any change writes an audit-trail row to data_health_events and — via the
 * hash-invalidation logic in _hash_audit_input — refreshes affected merchants
 * on their next dashboard load without consuming quota slots.
 *
 * Batch 3 (2026-07-22) — mirrors the backend `COMPREHENSIVE_EFFECTIVE_DATES`
 * defense-in-depth map. When admin picks ACTIVE for a state that is not yet
 * legally effective, the confirm() dialog surfaces the effective date up-front
 * so the admin sees the block *before* clicking OK. The server-side guard
 * still enforces the block — this is UX polish, not the safety mechanism.
 */
// Keep in sync with backend/routes/admin_routes.py :: COMPREHENSIVE_EFFECTIVE_DATES.
// Adding a new comprehensive state? Update BOTH maps in the same commit.
const COMPREHENSIVE_EFFECTIVE_DATES_UI = {
  CA: "2020-01-01", VA: "2023-01-01", CO: "2023-07-01",
  IA: "2025-01-01", IN: "2026-01-01", KY: "2026-01-01",
  MN: "2025-07-31", NE: "2025-01-01", TN: "2025-07-01",
  AL: "2027-05-01", OK: "2027-01-01", LA: "2027-01-01", VT: "2028-01-01",
};

function _formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch (_e) {
    return iso;
  }
}

function ScoringPolicySelect({ abbr, current, onChanged }) {
  const [saving, setSaving] = React.useState(false);
  const setPolicy = async (next) => {
    if (next === current) return;

    // Batch 3 UX polish — surface the effective-date guard in the confirm
    // dialog BEFORE the admin has to click OK and see a red toast.
    let notYetEffectiveWarning = "";
    if (next === "ACTIVE") {
      const effIso = COMPREHENSIVE_EFFECTIVE_DATES_UI[abbr];
      if (effIso) {
        const eff = new Date(effIso + "T00:00:00Z");
        if (eff > new Date()) {
          notYetEffectiveWarning =
            `\n\n⚠️  NOT YET LEGALLY EFFECTIVE\n` +
            `${abbr} takes effect ${_formatDate(effIso)}.\n` +
            `The server will REJECT this activation (HTTP 400) unless a force-override is used.\n` +
            `You should not activate a not-yet-effective law under normal circumstances.`;
        }
      }
    }

    if (!window.confirm(
      `Set ${abbr} scoring_policy to ${next}?\n\n` +
      (next === "ACTIVE"
        ? "Merchants selecting this state will get a scored Pillar 2 on next audit (Stage 4). Legacy merchants missing the compliance-profile questions will see Provisional · N/A instead."
        : "This state will fall back to the not_yet_scored branch for all merchants. Effective immediately on next dashboard load.") +
      notYetEffectiveWarning,
    )) return;
    setSaving(true);
    try {
      await adminApi.post("/admin/jurisdictions/set-scoring-policy", {
        abbreviation: abbr,
        scoring_policy: next,
      });
      toast.success(`${abbr}: scoring_policy → ${next}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update scoring policy");
    } finally {
      setSaving(false);
    }
  };
  const color = current === "ACTIVE" ? "var(--rg-sage)" : current === "DISABLED" ? "var(--rg-red)" : "var(--rg-muted)";
  return (
    <select
      value={current}
      onChange={(e) => setPolicy(e.target.value)}
      disabled={saving}
      className="h-8 text-xs font-mono px-2 rounded-sm border bg-[var(--rg-surface)] text-[var(--rg-text-strong)]"
      style={{ borderColor: color, color }}
      data-testid={`admin-scoring-policy-${abbr}`}
    >
      <option value="PENDING_REVIEW">PENDING_REVIEW</option>
      <option value="ACTIVE">ACTIVE</option>
      <option value="DISABLED">DISABLED</option>
    </select>
  );
}

